// Hideo - script editor's writing surface: closing the floating elements (block menu, link bubble, outline, toolbar
// drop-down and ⋮ panel) on a tap elsewhere or with Esc (before Esc closes the editor), and the toolbar following the
// cursor. Ported from the legacy editor/editor.js with the same listeners in the same order.
import { useToolbarStore } from '../../../stores/toolbar'
import { useTocStore } from '../../../stores/toc'
import { useCommentsStore } from '../../../stores/comments'
import { useDialogsStore } from '../../../stores/dialogs'
import { byId, pageRange } from './core'
import { blockMenuShown, dropDrag, hideBlockMenu } from './blocks'
import { hideLinkBubble, linkBubbleShown, placeLinkBubble } from './links'

/** Closes every floating element of the editor: menus, bubble, block handle, outline, ⋮, the comment being written
 *  (closing, deleting, switching to Markdown) */
export function edHideFloating(): void {
  const tb = useToolbarStore()
  tb.toggleMore(false); tb.hideDrop(); useCommentsStore().cancel(); useCommentsStore().toggle(false); hideBlockMenu(); hideLinkBubble(); dropDrag()
  byId('blockHandle').classList.add('hidden'); byId('blockDrop').classList.add('hidden'); useTocStore().toggle(false)
}

/** The listeners the legacy file added at the end, in its order: a tap elsewhere (document, capture phase), Esc
 *  (window, capture phase: docs/architecture.md, "Global keyboard shortcuts", step 1), resizing the window */
export function installFloating(doc: Pick<Document, 'addEventListener'>, win: Pick<Window, 'addEventListener'>): void {
  doc.addEventListener('pointerdown', e => {
    const tg = e.target as Element, toc = useTocStore()
    if (blockMenuShown() && !tg.closest('#blockMenu,#blockGrip')) hideBlockMenu()
    if (linkBubbleShown() && !tg.closest('#linkBubble') && !tg.closest('#scriptBody a')) hideLinkBubble()
    if (toc.open && !tg.closest('#scriptToc,#tocBtn')) toc.toggle(false)
  }, true)
  // Not while the teleprompter is on top: its own Esc (src/features/keyboard.ts) closes it first
  win.addEventListener('keydown', ev => {
    const e = ev as KeyboardEvent, dialogs = useDialogsStore()
    if (e.key !== 'Escape' || !dialogs.isOpen('editor') || dialogs.isOpen('teleprompter')) return
    const stop = () => { e.stopPropagation(); e.preventDefault() }
    const tb = useToolbarStore(), toc = useTocStore()
    if (tb.drop.kind !== '') { tb.hideDrop(); stop() }
    else if (tb.more) { tb.toggleMore(false); stop() }
    else if (blockMenuShown()) { hideBlockMenu(); stop() }
    else if (linkBubbleShown()) { hideLinkBubble(); stop() }
    else if (toc.open) { toc.toggle(false); stop() }
  }, true)
  win.addEventListener('resize', () => { placeLinkBubble(); hideBlockMenu() })
}

/** The toolbar follows the cursor in the page (selectionchange), and its drop-down and ⋮ panel close on a tap elsewhere
 *  (document, capture phase) */
export function installToolbarSync(doc: Pick<Document, 'addEventListener'>): void {
  doc.addEventListener('selectionchange', () => { if (pageRange()) useToolbarStore().updState() })
  doc.addEventListener('pointerdown', e => {
    const tg = e.target as Element, tb = useToolbarStore()
    if (tb.drop.kind !== '' && !tg.closest('#tbDropMenu,.tb-drop')) tb.hideDrop()
    if (tb.more && !tg.closest('#tbMorePanel,#tbMoreBtn,#tbDropMenu')) tb.toggleMore(false)
  }, true)
}
